/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useRef, useState } from "react";
import ct from "class-types.macro";
import { toast } from "@clarkinc/zest-design-system/ui";
import Modal, { Header, Footer, Body } from "~/common/ModalReact";
import loaderGif from "~/common/loader.gif";
import addToOrderGuide from "./addToOrderGuide";
import { OrderGuideData } from "./getOrderGuideData";
import OrderGuideOptions from "./OrderGuideOptions";
import CustomToast from "~/ProductDetails/UserLists/CustomToast";
interface Props {
  userIndex: string;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  orderGuideData: OrderGuideData;
  itemNumber: string;
  quantity: string;
  itemDescription: string;
  imagePath: string;
  hasOrderGuideData: boolean;
}
export default ({
  userIndex,
  isModalOpen,
  setIsModalOpen,
  orderGuideData,
  itemNumber,
  quantity,
  itemDescription,
  imagePath,
  hasOrderGuideData
}: Props) => {
  const hasSubordinates = orderGuideData.subordinateAccounts?.length > 0;
  const [errorMessage, setErrorMessage] = useState("");
  const [yourAccountCategory, setYourAccountCategory] = useState("");
  const [checkAll, setCheckAll] = useState(false);
  const [isInRemoveCheckAllEvent, setIsInRemoveCheckAllEvent] = useState(false);
  const [selectedOrderGuides, setSelectedOrderGuides] = useState<{
    [index: string]: {
      checked: boolean;
      category: string;
    };
  }>({});
  const applyToAllRef = useRef<HTMLInputElement>(null);
  const updateSelectedOrderGuides = (userId: string, category: string, checked: boolean) => {
    const newSelectedOrderGuides = {
      ...selectedOrderGuides
    };
    newSelectedOrderGuides[userId] = {
      category,
      checked
    };
    setSelectedOrderGuides(newSelectedOrderGuides);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const checkAndReturnAllOrderGuides = () => {
    const newOrderGuides: {
      [index: string]: {
        checked: boolean;
        category: string;
      };
    } = {
      ...selectedOrderGuides
    };
    newOrderGuides[userIndex] = {
      category: yourAccountCategory,
      checked: true
    };
    if (hasSubordinates) {
      // Don't override the category the user has selected...
      orderGuideData.subordinateAccounts.forEach(x => {
        const sub = newOrderGuides[x.userId];
        if (sub) {
          sub.checked = true;
        } else {
          newOrderGuides[x.userId] = {
            category: x.categories[0] || "",
            checked: true
          };
        }
      });
    }
    return newOrderGuides;
  };
  const handleRemoveApplyAllChange = () => {
    if (applyToAllRef.current) {
      applyToAllRef.current.checked = false;
    }
    setIsInRemoveCheckAllEvent(true);
    setCheckAll(false);
  };
  const handleCheckAllChange = () => {
    const newCheckAll = !checkAll;
    if (newCheckAll) {
      setSelectedOrderGuides(checkAndReturnAllOrderGuides());
    } else {
      setSelectedOrderGuides({});
    }
    setIsInRemoveCheckAllEvent(false);
    setCheckAll(newCheckAll);
  };
  const handleAddToOrderGuide = async () => {
    let newSelectedOrderGuides: {
      [index: string]: {
        checked: boolean;
        category: string;
      };
    } = {};
    if (checkAll) {
      newSelectedOrderGuides[userIndex] = {
        category: yourAccountCategory,
        checked: true
      };
      if (hasSubordinates) {
        orderGuideData.subordinateAccounts.forEach(x => {
          newSelectedOrderGuides[x.userId] = {
            category: yourAccountCategory,
            checked: true
          };
        });
      }
    } else {
      newSelectedOrderGuides = selectedOrderGuides;
    }
    const orderGuides = Object.keys(newSelectedOrderGuides).filter(k => newSelectedOrderGuides[k]?.checked).map(k => ({
      userId: k,
      categoryName: newSelectedOrderGuides[k]?.category || "",
      quantity,
      itemNumber
    }));
    if (orderGuides.length < 1) {
      setErrorMessage("At least 1 order guide needs to be selected");
      return;
    }
    const missingCategoryNameOrderGuides = orderGuides.filter(x => !x.categoryName);
    if (missingCategoryNameOrderGuides.length > 0) {
      let accountName = "Your account";
      if (userIndex !== missingCategoryNameOrderGuides[0]?.userId) {
        const sub = orderGuideData.subordinateAccounts.find(x => x.userId === missingCategoryNameOrderGuides[0]?.userId);
        accountName = sub?.company || sub?.name || "";
      }
      setErrorMessage(`Account "${accountName}" is selected but doesn't have an order guide specified`);
      return;
    }
    setErrorMessage("");
    closeModal();
    try {
      const orderGuidesAdded = await addToOrderGuide(orderGuides);
      orderGuidesAdded.forEach(orderGuide => {
        toast(<CustomToast title={`Item added to ${orderGuide.categoryName}`} body={`${itemDescription} was added to ${orderGuide.categoryName}`} imageUrl={imagePath} buttonLinkUrl="/myproducts.html?ignore_timeout=true" buttonLinkText="View Order Guide" />);
      });
    } catch (e) {
      toast.error("", {
        title: "There was an error adding the item to an order guide"
      });
    }
  };
  return <>
      <Modal isOpen={isModalOpen} onRequestClose={closeModal} className="max-h-screen md:max-w-5xl">
        <Header closeModal={closeModal} className="py-4 pl-8 bg-gray-100">
          <h3 className="mb-0 text-lg leading-none">Add Item to Order Guide</h3>
        </Header>

        <Body>
          {hasOrderGuideData ? <>
              {errorMessage && <div className="guide-add__error">{errorMessage}</div>}
              <div className="form">
                {hasSubordinates && <div className="guide-add__all">
                    <div className="form__multiselect-container">
                      <div className="form__multiselect-element">
                        <input type="checkbox" className="form__multiselect-input" id="applyToAll" data-testid="apply-to-all-checkbox" onChange={handleCheckAllChange} ref={applyToAllRef} />
                        <label htmlFor="applyToAll" className="form__check form__multiselect" />
                      </div>
                      <label className="form__multiselect-text" htmlFor="applyToAll">
                        Apply to All Accounts
                      </label>
                    </div>
                  </div>}
                <div className="guide-add__row box form__group">
                  <OrderGuideOptions accountName="Your account" userId={userIndex || ""} categories={orderGuideData.categories} updateSelectedOrderGuides={updateSelectedOrderGuides} selectedOrderGuides={selectedOrderGuides} checkAll={checkAll} disabled={false} setYourAccountCategory={setYourAccountCategory} removeApplyAll={handleRemoveApplyAllChange} isInRemoveCheckAllEvent={isInRemoveCheckAllEvent} />
                </div>
                {hasSubordinates && <div className="guide-add__sub">
                    <h3 className="guide-add__subtitle">Your Sub Accounts</h3>
                    <div>
                      {orderGuideData.subordinateAccounts.map(sub => <div className="guide-add__row box form__group" key={sub.userId}>
                          <OrderGuideOptions accountName={sub.company || sub.name} userId={sub.userId || ""} categories={sub.categories} updateSelectedOrderGuides={updateSelectedOrderGuides} selectedOrderGuides={selectedOrderGuides} checkAll={checkAll} disabled={checkAll} removeApplyAll={handleRemoveApplyAllChange} isInRemoveCheckAllEvent={isInRemoveCheckAllEvent} />
                        </div>)}
                    </div>
                  </div>}
              </div>
            </> : <div className={`${ct("flex", "justify-center", "align-middle")}`}>
              <img src={loaderGif} alt="waiting" />
            </div>}
        </Body>
        <Footer>
          {hasOrderGuideData && <button type="button" className="btn btn-primary pl-8 pr-8 mr-2" onClick={handleAddToOrderGuide} data-testid="submit-add-to-order-guide-button">
              Add to Order Guide
            </button>}
          <button type="button" className="btn btn-light" onClick={closeModal}>
            Close
          </button>
        </Footer>
      </Modal>
    </>;
};